import type {
  EducationItem,
  GetV1LinksByProfileLinkResponse,
  HighlightedProject,
  Profile,
  ProfileLinkConfig,
  SideProject,
  SkillCategoryBlock,
  WorkplaceItem,
} from "../../../client";

/**
 * ProfileViewModel — the single, resolved representation of a profile link that
 * every CV template consumes.
 *
 * Step 1a of the CV-template refactor (see /CV_TEMPLATES_PLAN.md): this pulls all
 * the data/config resolution that today lives *inline and duplicated* inside
 * `ProfileContainer` and `PDFProfileContainer` into one pure function, so that
 * templates never re-derive data.
 *
 * Faithfulness note: this step is a straight extraction — the output must let a
 * template reproduce today's markup exactly. Some raw config (e.g. the skills
 * show/hide conditions, which are intentionally asymmetric between top and bottom)
 * is therefore exposed as-is rather than collapsed into a single boolean. Evolving
 * this into a fully declarative `sections[]` array is a later step.
 */

export type SectionKey =
  | "profile_summary"
  | "work_experience"
  | "projects"
  | "education"
  | "skill_stack"
  | "highlighted_projects";

export interface ResolvedPersonalInfo {
  name: string;
  title: string;
  pitch: string;
  editedPitch?: string;
  /** Per-link overrides (not in the generated type yet — read via cast). */
  editedName?: string;
  editedTitle?: string;
  profilePhotoId?: string;
  useProfilePicture: boolean;
  roundedProfilePicture: boolean;
  hidePersonalInfo: boolean;
  useBorder: boolean;
  hidePitch: boolean;
}

export interface ResolvedSkills {
  /** True when at least one skill category block contains skill tags. */
  skillTagsExist: boolean;
  displaySkillsSummary: boolean;
  placeInTop: boolean;
  skillCategoryBlocks: SkillCategoryBlock[];
  displayIcons: boolean;
  /** "" → render as SkillsBlock, otherwise render as SkillSummary. */
  sortedMode: string;
  /** Layout: true → vertical list, false → skill cloud (chips). Takes
   *  precedence over sortedMode when set. */
  displayAsList: boolean;
  /** Chip ordering: "" (alphabetical, default) or "mostMentioned". */
  skillOrder: string;
  /** name (lowercased) → mention count, from the profile's skill summary.
   *  Used to order chips when skillOrder is "mostMentioned". */
  skillCounts: Record<string, number>;
}

export interface OrganizationTheme {
  organizationId?: string;
  organizationName?: string;
  colors: { primary: string; secondary: string; accent: string };
  logoId?: string;
  iconLogoId?: string;
}

/** Logo + contact for the org, so templates can render branding themselves. */
export interface ProfileBranding {
  logoUrl?: string;
  organizationName?: string;
  contact: {
    name?: string;
    email?: string;
    phone?: string;
    website?: string;
  };
}

/** Public asset base for logo ids (Cloudflare R2). */
const ASSET_BASE_URL = "https://pub-4b15203a4a0f4b26ab71deeffce0164a.r2.dev/";

export interface ProfileViewModel {
  language: string;
  /** Convenience: linkConfigData.language === "english". */
  isEnglish: boolean;

  personalInfo: ResolvedPersonalInfo;
  theme: OrganizationTheme | null;
  /** Org logo + contact, for templates that render branding themselves. */
  branding: ProfileBranding;

  /** profileSummaryConfig.showProfileSummary && !profileMissingInformation. */
  showProfileSummary: boolean;
  /** highlightedProjects filtered to those with `included`. Statsig gating stays in the view. */
  highlightedProjects: HighlightedProject[];
  skills: ResolvedSkills;
  /** Filtered (allowed ids), edited-applied, custom-ordered workplaces. */
  workplaces: WorkplaceItem[];
  /** Filtered (allowed ids), edited-applied side projects. */
  projects: SideProject[];
  educations: EducationItem[];
  showEducationSection: boolean;

  sectionTitles: Record<SectionKey, string>;

  getWorkplaceName: (workplaceId: string) => string;
  getWorkplaceNameFromProject: (projectId: string) => string;

  /** Organization-level pagination flags (consumed by PDF layout). */
  avoidWorkplacePageBreak: boolean;
  avoidWorkplaceDescriptionPageBreak: boolean;

  /** Passthrough for child components that still read raw config/profile directly. */
  linkConfig?: ProfileLinkConfig;
  profile?: Profile;
  accountProfileId: string;
  profilePhotoId?: string;
}

const SECTION_TITLES: Record<SectionKey, { en: string; sv: string }> = {
  profile_summary: { en: "Profile Summary", sv: "Sammanfattning" },
  work_experience: { en: "Work experience", sv: "Erfarenhet" },
  projects: { en: "Projects", sv: "Projekt" },
  education: {
    en: "Education, courses and certificates",
    sv: "Utbildning, kurser och certifieringar",
  },
  skill_stack: { en: "Skill stack", sv: "Kompetenser" },
  highlighted_projects: { en: "Highlighted projects", sv: "Utvalda projekt" },
};

export function buildViewModel(
  link: GetV1LinksByProfileLinkResponse,
): ProfileViewModel {
  const accountProfile = link.accountProfile;
  const profile = accountProfile.profile_data as Profile | undefined;
  const config = accountProfile.link_config_data as
    | ProfileLinkConfig
    | undefined;
  const organization = accountProfile.organization ?? null;

  const isEnglish = config?.language === "english";

  // --- Theme -----------------------------------------------------------------
  const theme: OrganizationTheme | null = organization?.id
    ? {
        organizationId: organization.id,
        organizationName: organization.name,
        colors: {
          primary: organization.primaryColor || "#3b82f6",
          secondary: organization.secondaryColor || "#000",
          accent: "#3b82f6",
        },
        logoId: organization.logoId ?? undefined,
        iconLogoId: organization.iconLogoId ?? undefined,
      }
    : null;

  // --- Workplaces: filter → apply edited → custom order ----------------------
  const editedWorkplaces = config?.editedWorkplaces ?? [];
  const allowedWorkplaceIds = config?.profileLinkWorkplaceIds ?? [];
  const customOrder = config?.customWorkplaceOrder;

  let workplaces: WorkplaceItem[] = profile?.workplaces
    ? profile.workplaces
        .filter((wp) => allowedWorkplaceIds.includes(wp.id))
        .map((wp) => editedWorkplaces.find((w) => w.id === wp.id) ?? wp)
    : [];

  if (customOrder && customOrder.length > 0) {
    const orderMap = new Map<string, number>();
    customOrder.forEach((item, index) => orderMap.set(item.id, index));
    workplaces = [...workplaces].sort((a, b) => {
      const orderA = orderMap.has(a.id)
        ? orderMap.get(a.id)!
        : Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.has(b.id)
        ? orderMap.get(b.id)!
        : Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  // --- Side projects: filter → apply edited ----------------------------------
  const editedSideProjects = config?.editedSideProjects ?? [];
  const allowedProjectIds =
    config?.profileLinkProjects?.map((p) => p.projectId) ?? [];

  const projects: SideProject[] = profile?.side_projects_section?.side_projects
    ? profile.side_projects_section.side_projects
        .filter((p) => allowedProjectIds.includes(p.id))
        .map((p) => editedSideProjects.find((w) => w.id === p.id) ?? p)
    : [];

  // --- Skills ----------------------------------------------------------------
  const skillsConfig = config?.skillsSummaryConfig;
  const skillCategoryBlocks = skillsConfig?.skillCategoryBlocks ?? [];
  const skillTagsExist =
    skillCategoryBlocks.length > 0 &&
    skillCategoryBlocks.some((block) => block.skillTags.length > 0);

  // Mention counts per skill, from the profile's precomputed skill summary.
  const skillCounts: Record<string, number> = {};
  for (const sc of profile?.profileSkillSummary ?? []) {
    if (sc.name) skillCounts[sc.name.toLowerCase()] = sc.totalCount ?? 0;
  }

  const skills: ResolvedSkills = {
    skillTagsExist,
    displaySkillsSummary: skillsConfig?.displaySkillsSummary ?? false,
    placeInTop: skillsConfig?.placeInTop ?? false,
    skillCategoryBlocks,
    displayIcons: skillsConfig?.displayIcons ?? true,
    sortedMode: skillsConfig?.sortedMode ?? "",
    displayAsList: skillsConfig?.displayAsList ?? false,
    skillOrder:
      (skillsConfig as { skillOrder?: string } | undefined)?.skillOrder ?? "",
    skillCounts,
  };

  // --- Name lookups ----------------------------------------------------------
  const getWorkplaceName = (workplaceId: string): string => {
    const match = profile?.workplaces.find((wp) => wp.id === workplaceId);
    return match?.name ?? "";
  };

  const getWorkplaceNameFromProject = (projectId: string): string => {
    const project = profile?.side_projects_section?.side_projects.find(
      (p) => p.id === projectId,
    );
    if (!project) return "";
    return getWorkplaceName(project.workplaceId ?? "");
  };

  // --- Section titles --------------------------------------------------------
  const sectionTitles = Object.fromEntries(
    (Object.keys(SECTION_TITLES) as SectionKey[]).map((key) => [
      key,
      isEnglish ? SECTION_TITLES[key].en : SECTION_TITLES[key].sv,
    ]),
  ) as Record<SectionKey, string>;

  return {
    language: config?.language ?? "",
    isEnglish,

    personalInfo: {
      name: profile?.name ?? "",
      title: profile?.title ?? "",
      pitch: profile?.pitch ?? "",
      editedPitch: config?.editedPitch,
      editedName: (config as { editedName?: string } | undefined)?.editedName,
      editedTitle: (config as { editedTitle?: string } | undefined)?.editedTitle,
      profilePhotoId: accountProfile.profilePhotoId ?? undefined,
      useProfilePicture: config?.useProfilePicture ?? false,
      roundedProfilePicture:
        config?.profileLinkPersonalInfoConfig?.roundMode ?? false,
      hidePersonalInfo: !config?.profileLinkPersonalInfoConfig?.showPersonalInfo,
      useBorder: config?.profileLinkPersonalInfoConfig?.useBorder ?? false,
      hidePitch: !config?.profileLinkPersonalInfoConfig?.showPitch,
    },

    theme,

    branding: {
      logoUrl: organization?.logoId
        ? ASSET_BASE_URL + organization.logoId
        : undefined,
      organizationName: organization?.name,
      contact: {
        name: organization?.salesContact ?? undefined,
        email: organization?.email ?? undefined,
        phone: organization?.phoneNumber ?? undefined,
        website: organization?.website ?? undefined,
      },
    },

    showProfileSummary:
      !!config?.profileSummaryConfig?.showProfileSummary &&
      !config?.profileMissingInformation,
    highlightedProjects: (config?.highlightedProjects ?? []).filter(
      (hp) => hp.included,
    ),
    skills,
    workplaces,
    projects,
    educations: profile?.educations ?? [],
    showEducationSection: config?.displayEducationSection ?? false,

    sectionTitles,

    getWorkplaceName,
    getWorkplaceNameFromProject,

    avoidWorkplacePageBreak: organization?.avoidWorkplacePageBreak ?? false,
    avoidWorkplaceDescriptionPageBreak:
      organization?.avoidWorkplaceDescriptionPageBreak ?? false,

    linkConfig: config,
    profile,
    accountProfileId: accountProfile.id,
    profilePhotoId: accountProfile.profilePhotoId ?? undefined,
  };
}
